import { useEffect, useRef } from 'react';
import { useAppSelector, useAppDispatch } from 'flow/hooks/useRedux';
import {
    toggleSenderMeetingAudioActive,
    toggleSenderVideoActive,
} from 'flow/actions/meeting-actions';
import {
    selectSenderAudioActive,
    selectVideoAllowed,
} from 'flow/selectors/meeting-selectors';

/**
 * Hook that handles keyboard shortcuts for meeting controls:
 * - Ctrl/Cmd + D: Toggle microphone
 * - Ctrl/Cmd + E: Toggle camera (if allowed)
 * - Space (hold): Push-to-talk (unmute while held, mute on release)
 */

export const useMeetingKeyboardShortcuts = () => {
    const dispatch = useAppDispatch();

    const audioActive = useAppSelector(selectSenderAudioActive);
    const videoAllowed = useAppSelector(selectVideoAllowed);

    // Refs to access latest values in event handlers without re-registering listeners
    const audioActiveRef = useRef(audioActive);
    const spaceToTalkRef = useRef(false);
    const videoAllowedRef = useRef(videoAllowed);

    // Keep refs in sync with state
    useEffect(() => {
        audioActiveRef.current = audioActive;
    }, [audioActive]);

    useEffect(() => {
        videoAllowedRef.current = videoAllowed;
    }, [videoAllowed]);

    useEffect(() => {
        const onKeyDown = (event: KeyboardEvent) => {
            const superKey = event.ctrlKey || event.metaKey;

            if ((event.key === 'D' || event.key === 'd') && superKey) {
                event.preventDefault();
                dispatch(toggleSenderMeetingAudioActive());
            } else if ((event.key === 'E' || event.key === 'e') && superKey) {
                event.preventDefault();
                if (videoAllowedRef.current) {
                    void dispatch(toggleSenderVideoActive());
                }
            } else if (event.code === 'Space') {
                const elementTagName = (document.activeElement as HTMLElement)
                    ?.tagName;
                const fieldToWriteIsFocused =
                    elementTagName === 'TEXTAREA' || elementTagName === 'INPUT';

                if (!fieldToWriteIsFocused) {
                    event.preventDefault();
                    if (!event.repeat && !audioActiveRef.current) {
                        dispatch(toggleSenderMeetingAudioActive());
                        spaceToTalkRef.current = true;
                    }
                }
            }
        };

        const onKeyUp = (event: KeyboardEvent): void => {
            if (event.code === 'Space' && spaceToTalkRef.current) {
                event.preventDefault();
                dispatch(toggleSenderMeetingAudioActive());
                spaceToTalkRef.current = false;
            }
        };

        document.addEventListener('keydown', onKeyDown);
        document.addEventListener('keyup', onKeyUp);

        return () => {
            document.removeEventListener('keydown', onKeyDown);
            document.removeEventListener('keyup', onKeyUp);
        };
    }, [dispatch]);
};

export default useMeetingKeyboardShortcuts;
